// src/components/sponsor/auth/SponsorLogin.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { loginSponsor, clearSponsorError } from "@/store/slices/sponsorSlice";
import { Briefcase, Mail, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import ParticleBackground from "@/components/home/ParticleBackground";
import Button from "@/components/common/Button";
import TextInput from "@/components/common/TextInput";

const SponsorLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { error } = useSelector((state) => state.sponsor);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { email: "", password: "" },
  });

  React.useEffect(() => {
    dispatch(clearSponsorError());
  }, [dispatch]);

  const onSubmit = async (data) => {
    const result = await dispatch(loginSponsor(data));
    if (loginSponsor.fulfilled.match(result)) {
      navigate("/sponsor/dashboard");
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center py-12 px-4 overflow-hidden">
      <ParticleBackground />
      
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Sponsor Portal
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Login to access sponsor dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <TextInput
            label="Email Address"
            type="email"
            icon={<Mail className="w-5 h-5" />}
            placeholder="sponsor@company.com"
            error={errors.email?.message}
            {...register("email", {
              required: "Email is required",
              pattern: { value: /\S+@\S+\.\S+/, message: "Invalid email" },
            })}
          />

          <div className="relative">
            <TextInput
              label="Password"
              type={showPassword ? "text" : "password"}
              icon={<Lock className="w-5 h-5" />}
              placeholder="Enter your password"
              error={errors.password?.message}
              {...register("password", {
                required: "Password is required",
                minLength: { value: 6, message: "Minimum 6 characters" },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </motion.div>
          )}

          <Button
            type="submit"
            loading={isSubmitting}
            className="w-full py-3 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700"
            size="lg"
          >
            Sign In
          </Button>
        </form>

        <div className="text-center mt-6">
          <p className="text-gray-600 dark:text-gray-400">
            Want to become a sponsor?{" "}
            <Link
              to="/sponsor/apply"
              className="text-teal-600 hover:text-teal-700 dark:text-teal-400 font-semibold"
            >
              Apply here
            </Link>
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            Not a sponsor?
          </p>
          <div className="flex gap-3">
            <Link
              to="/login"
              className="flex-1 py-2.5 text-center text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              User Login
            </Link>
            <Link
              to="/organizer/login"
              className="flex-1 py-2.5 text-center text-sm font-medium border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
            >
              Organizer Login
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SponsorLogin;
